import type { Translations } from './types'

/* ── English ─────────────────────────────────────────────────── */
export const en: Translations = {
  profile: {
    title:         'My Profile',
    backBtn:       '← Home',
    statsTitle:    'My Statistics',
    sessionsTitle: 'Recent Activity',
    accountTitle:  'My Account',
    editName:      'Edit name',
    saveName:      'Save',
    cancelEdit:    'Cancel',
    tierLabel:     'Plan',
    emailLabel:    'Email',
    memberSince:   'Member since',
    noSessions:    "You haven't played any games yet.",
    completed:     'Completed',
    abandoned:     'Abandoned',
    score:         'pts',
    moves:         'moves',
    viewPlans:     'View plans & pricing',
    tierNames:      { BASIC: 'Basic', PLUS: 'Plus', PREMIUM: 'Premium' },
    languageTitle:  'Language',
    languageEs:     'Español',
    languageEn:     'English',
    professionalModeTitle:      'Professional Mode',
    professionalModeActive:     'Professional mode is active on your account.',
    disableProfessional:        'Disable professional mode',
    disableProfessionalHint:    'This will remove access to patient profiles and activity routines.',
    professionalModeTeaser:     'Professional Mode 👩‍⚕️',
    professionalModeTeaserDesc: 'With Professional Mode you can create individual profiles for your patients or family members, design personalised routines and track their progress over time. Available on the Premium plan.',
    professionalModeUpgradeBtn: 'See Premium plan',
    statsChartsTitle: 'My Statistics',
    helpTitle:        'Need Help?',
    helpName:         'Name',
    helpMessage:      'Message',
    helpSubmit:       'Send message',
    helpSending:      'Sending…',
    helpSuccess:      '✅ Message sent. We\'ll get back to you soon.',
    helpError:        'Could not send your message. Please try again.',
  },
  auth: {
    loginTab:         'Sign In',
    signupTab:        'Sign Up',
    loginWelcome:     'Welcome back',
    loginSubtitle:    'Continue your exercises',
    signupWelcome:    'Join Tengo de TO',
    signupSubtitle:   'Create your free account',
    nameLabel:        'Name',
    namePlaceholder:  'Your name',
    emailLabel:       'Email',
    emailPlaceholder: 'your@email.com',
    passwordLabel:    'Password',
    passwordPlaceholder: '••••••••',
    loginBtn:         'Sign In',
    signupBtn:        'Create Account',
    loggingIn:        'Signing in…',
    signingUp:        'Creating account…',
    switchToSignup:   "Don't have an account? Sign up",
    switchToLogin:    'Already have an account? Sign in',
    errInvalidCreds:  'Invalid email or password',
    errEmailExists:   'Email already registered',
    errPasswordMin:   'Password must be at least 8 characters',
    errGeneric:       'Something went wrong. Please try again.',
    errNotVerified:   'You need to verify your email before signing in. Check your inbox.',
    logout:           'Sign Out',
    verifyTitle:      'Check your inbox!',
    verifySubtitle:   'We sent you a verification link. Click it to activate your account.',
    verifyResend:     'Resend link',
    verifyResendSent: 'Link resent. Check your inbox.',
    verifyResendWait: 'Please wait a moment before resending.',
    forgotLink:       'Forgot your password?',
    forgotTitle:      'Reset password',
    forgotSubtitle:   'Enter your email and we\'ll send you a link to reset your password.',
    forgotBtn:        'Send link',
    forgotSending:    'Sending…',
    forgotSent:       'If the email is registered, you\'ll receive a link shortly.',
    resetTitle:       'New password',
    resetSubtitle:    'Choose a secure password for your account.',
    resetNewPassword: 'New password',
    resetConfirm:     'Confirm password',
    resetBtn:         'Reset password',
    resetSaving:      'Saving…',
    resetSuccess:     'Password reset. You can now sign in.',
    resetMismatch:    'Passwords do not match.',
    resetExpired:     'The link has expired or is invalid. Please request a new one.',
    backToLogin:      'Back to sign in',
    termsAccept:      'I have read and accept the',
    termsLink:        'Terms and Conditions',
    termsRequired:    'You must accept the Terms and Conditions to continue.',
    showPassword:     'Show password',
    hidePassword:     'Hide password',
  },
  app: {
    name: 'Tengo de TO',
    tagline: 'Exercises for an active and happy mind',
    footer: 'Tengo de TO · Cognitive Wellness',
  },
  dashboard: {
    stats: { today: 'Sessions Today', streak: 'Week Streak', best: 'Best Score', percentile: 'Score Rank' },
    activitiesTitle: 'Activities',
    comingSoon: 'Soon',
    downloadsAriaLabel: 'View downloads',
    downloadsBtnText:   'Printable activities',
    activitiesGrid:     'Available activities',
    games: {
      memory:       { label: 'Memory',        description: 'Match pairs of cards. Train your visual memory and concentration.' },
      wordmatch:    { label: 'Words',          description: 'Images and definitions to enrich your vocabulary.' },
      cutlery:      { label: 'Drawer',         description: 'Sort the cutlery into its compartments. Exercise order and classification.' },
      wordsearch:   { label: 'Word Search',    description: 'Find hidden words in the grid. Train your focus and attention.' },
      sudoku:       { label: 'Sudoku',         description: 'Fill the grid with numbers. Exercise logic and concentration.' },
      maths:        { label: 'Maths',          description: 'Solve addition, subtraction, multiplication and division operations.' },
      shoppinglist: { label: 'Shopping List',  description: 'Memorise the shopping list and find the right products at the supermarket.' },
      eurmoney:     { label: 'Money',          description: 'Learn to recognise coins and notes and practise handling euros.' },
      pillbox:      { label: 'Pillbox',        description: 'Read the doctor\'s guide and organise the pills into each compartment correctly.' },
      attention:    { label: 'Attention',      description: 'Read the rules and mark the words in the cloud: circle, underline or cross out.' },
      symmetry:     { label: 'Symmetry',       description: 'Complete the missing half of the pixel drawing to create a symmetrical image.' },
      whats_missing: { label: "What's Missing?", description: 'Compare two groups of images and find the ones that do not appear in the other group.' },
      roman:        { label: 'Roman Numbers',  description: 'Read the number and choose or write its Roman numeral equivalent.' },
      crosswords:   { label: 'Crossword',      description: 'Connect letters in the circle to form words and complete the crossword.' },
      hours:        { label: 'The Clock',     description: 'Read analog and digital clocks and express the time in Spanish words.' },
      synonyms:     { label: 'Synonyms',      description: 'Find the synonym or antonym of each word and enrich your vocabulary.' },
      puzzle:       { label: 'Puzzle',        description: 'Join the image pieces together. Train your visual perception and memory.' },
      chess:        { label: 'Chess',         description: 'Recognize pieces, learn their moves, and solve classic chess puzzles.' },
      numtarget:    { label: 'Magic Number',  description: 'Use the number cards and operations to reach the target number.' },
    },
  },
  shoppinglist: {
    title:                 'Shopping List',
    subtitle:              'Memorise the list and pick the right products',
    backToDashboard:       '← Home',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start →',
    loading:               'Loading…',
    selected:              'picked',
    time:                  'time',
    memoriseTitle:         'Your shopping list',
    memoriseHint:          'Remember these items before heading to the supermarket',
    readyBtn:              'I\'m ready! →',
    cartTitle:             'Already in your trolley',
    optionsTitle:          'What are you still missing?',
    instructionPick:       'Select the {n} products that are missing from your list',
    checkBtn:              'Check →',
    previewListTitle:      'List',
    previewCartTitle:      'Supermarket',
    difficulty: {
      easy:   { label: 'Easy',   hint: 'List of 5 · 2 missing · 5 options' },
      medium: { label: 'Medium', hint: 'List of 7 · 3 missing · 7 options' },
      hard:   { label: 'Hard',   hint: 'List of 9 · 4 missing · 9 options' },
    },
    complete: {
      title:         'Well done!',
      subtitle:      'You got {correct} out of {total} items right',
      accuracyLabel: 'Accuracy',
      timeLabel:     'Time',
      levelLabel:    'Level',
      playAgain:     'Play again',
      changeLevel:   'Change level',
    },
  },
  eurmoney: {
    title:                 'Money',
    subtitle:              'Practise handling euro coins and notes',
    backToDashboard:       '← Home',
    chooseModeTitle:       'Choose a mode',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start →',
    loading:               'Loading…',
    noValues:              'The theme images have no value assigned. Set values in the admin panel.',
    moves:                 'moves',
    time:                  'time',
    round:                 'question',
    modes: {
      identify: { label: 'What coin is it?',  description: 'Look at the image and identify the coin or note' },
      count:    { label: 'How much is it?',   description: 'Add up the value of all the coins and notes' },
      pay:      { label: 'Pay the price',     description: 'Add coins and notes to the dish to reach the exact price' },
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: '5 questions · small amounts' },
      medium: { label: 'Medium', hint: '7 questions · up to €5.00' },
      hard:   { label: 'Hard',   hint: '10 questions · up to €20.00' },
    },
    instructionIdentify:   'How much is this coin or note worth?',
    instructionCount:      'Add up the value of all the coins and notes',
    instructionPay:        'Add coins to the dish to pay the exact price',
    instructionPayOver:    "You've gone over! Remove a coin",
    instructionPayCorrect: "Correct! That's exactly the right amount",
    dishLabel:             'Your dish',
    dishTotal:             'Total',
    clearBtn:              'Clear',
    complete: {
      title:        'Congratulations!',
      subtitle:     'You got {correct} out of {total} questions right',
      correctLabel: 'Correct',
      movesLabel:   'Moves',
      timeLabel:    'Time',
      levelLabel:   'Level',
      playAgain:    'Play again',
      changeLevel:  'Change level',
      home:         '← Home',
    },
  },

  pillbox: {
    title:                 'Pillbox',
    subtitle:              'Organise your pills following the doctor\'s guide',
    backToDashboard:       '← Home',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start →',
    moves:                 'actions',
    time:                  'time',
    guidelineTitle:        'Doctor\'s guide',
    pillboxTitle:          'Pillbox',
    bottlesTitle:          'Bottles',
    columnMed:             'Medicine',
    columnPill:            '💊',
    instructionSelect:     'Tap a bottle to select it',
    instructionPlace:      'Tap a compartment to add a pill',
    checkBtn:              'Check →',
    wrongFeedback:         'Some compartments are not correct. Try again!',
    schedules: {
      breakfast: 'Breakfast',
      lunch:     'Lunch',
      merienda:  'Snack',
      dinner:    'Dinner',
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: '2 medicines · no extra bottles' },
      medium: { label: 'Medium', hint: '3 medicines · 1 distractor bottle' },
      hard:   { label: 'Hard',   hint: '4 medicines · 2 distractor bottles' },
    },
    complete: {
      title:           'Well done!',
      subtitle:        'You filled the pillbox correctly',
      movesLabel:      'Actions',
      timeLabel:       'Time',
      levelLabel:      'Level',
      playAgain:       'Play again',
      changeLevel:     'Change level',
      backToDashboard: '← Home',
    },
  },

  attention: {
    title:                 'Attention',
    subtitle:              'Read the rules and mark the words correctly',
    subtitleNumbers:       'Read the rules and mark the numbers correctly',
    backToDashboard:       '← Home',
    chooseModeTitle:       'Choose mode',
    modeWords:             'Words',
    modeNumbers:           'Numbers',
    chooseThemesTitle:     'Add themes (optional)',
    chooseThemesHint:      'Enable themes to include their words and category conditions',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start →',
    moves:                 'taps',
    time:                  'time',
    checkBtn:              'Check →',
    instructionPlay:       'Tap each word to cycle through circle, underline and strikethrough',
    instructionPlayNumbers:'Tap each number to cycle through circle, underline and strikethrough',
    previewHint:           'Circle, underline or cross out according to the rules',
    conditions: {
      is_odd:         'Odd',
      is_even:        'Even',
      gt:             'Greater than {x}',
      lt:             'Less than {x}',
      digit_sum_gt:   'Digit sum greater than {y}',
      digit_count_eq: '{n} digits',
      digit_count_lt: 'Fewer than {n} digits',
      digit_count_gt: 'More than {n} digits',
      multiple_of:    'Multiples of {x}',
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: '20 items · 1 rule' },
      medium: { label: 'Medium', hint: '40 items · 2 rules' },
      hard:   { label: 'Hard',   hint: '60 items · 3 rules' },
    },
    complete: {
      title:         'Well done!',
      subtitle:      'You completed the attention exercise',
      accuracyLabel: 'Accuracy',
      movesLabel:    'Taps',
      timeLabel:     'Time',
      levelLabel:    'Level',
      playAgain:     'Play again',
      changeLevel:   'Change level',
    },
  },

  symmetry: {
    title:                          'Symmetry',
    subtitle:                       'Complete the pixel drawing',
    backToDashboard:                '← Home',
    chooseTypeTitle:                'Choose type',
    chooseModeTitle:                'Choose axis',
    chooseDifficultyTitle:          'Choose difficulty',
    startBtn:                       'Start →',
    moves:                          'taps',
    time:                           'time',
    progress:                       'correct cells',
    instructionPaint:               'Pick a colour and tap cells to complete the symmetry',
    instructionComplete:            'Symmetry complete!',
    instructionPaintTranslation:    'Pick a colour and tap cells to continue the pattern',
    instructionCompleteTranslation: 'Pattern complete!',
    eraser:                         'Eraser',
    colorPalette:                   'Colour',
    types: {
      symmetry:    { label: 'Symmetry 🪞',    description: 'Mirror the pattern across an axis' },
      translation: { label: 'Translation →', description: 'Copy the pattern to the other half' },
    },
    modes: {
      vertical:                 { label: 'Vertical ↔',   description: 'Complete the right half' },
      horizontal:               { label: 'Horizontal ↕', description: 'Complete the bottom half' },
      quad:                     { label: 'Quadrant ✦',   description: 'Complete the remaining three quarters' },
      'translation-vertical':   { label: 'Vertical →',   description: 'Copy pattern to the right half' },
      'translation-horizontal': { label: 'Horizontal ↓', description: 'Copy pattern to the bottom half' },
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: '8×8 grid · black only' },
      medium: { label: 'Medium', hint: '12×12 grid · 4 colours' },
      hard:   { label: 'Hard',   hint: '16×16 grid · 6 colours' },
    },
    complete: {
      title:           'Perfect!',
      subtitle:        'You completed the symmetry',
      movesLabel:      'Taps',
      timeLabel:       'Time',
      levelLabel:      'Level',
      playAgain:       'Play again',
      changeLevel:     'Change level',
      backToDashboard: '← Home',
    },
  },

  whatsMissing: {
    title:                 "What's Missing?",
    subtitle:              'Find the differences between the two groups',
    backToDashboard:       '← Home',
    chooseModeTitle:       'Choose image type',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start →',
    moves:                 'taps',
    time:                  'time',
    instructionEasy:       'Tap the {n} items in Group A that are not in Group B',
    instructionBidi:       '{n} unique items left to find across both groups',
    panelSelect:           'select here',
    panelRef:              'reference',
    modes: {
      emoji:  { label: 'Emojis',  description: 'Images and symbols' },
      shapes: { label: 'Shapes',  description: 'Coloured figures' },
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: '10 items · 3 differences · one direction' },
      medium: { label: 'Medium', hint: '15 items · 5 differences · both groups' },
      hard:   { label: 'Hard',   hint: '20 items · 7 differences · both groups' },
    },
    complete: {
      title:           'Excellent!',
      subtitle:        'You found all the differences',
      movesLabel:      'Taps',
      timeLabel:       'Time',
      levelLabel:      'Level',
      foundLabel:      'Found',
      playAgain:       'Play again',
      changeLevel:     'Change level',
      backToDashboard: '← Home',
    },
  },

  hours: {
    title:                 'The Clock',
    subtitle:              'Read the time on a clock and express it in words',
    backToDashboard:       '← Home',
    chooseModeTitle:       'Choose a mode',
    chooseDifficultyTitle: 'Choose difficulty',
    reverseLabel:          'Reverse mode',
    reverseHint:           'You see the written time and choose or set the clock',
    startBtn:              'Start →',
    question:              'question',
    moves:                 'attempts',
    instructionRead:       'What time does the clock show?',
    instructionType:       'Write the time you see on the clock in Spanish',
    instructionReverse:    'Which clock shows this time?',
    instructionSet:        'Set the clock to the given time',
    exampleHint:           'Format',
    inputPlaceholder:      'quarter past three',
    submitBtn:             'Check →',
    dragHint:              'Drag the H (hour) and M (minute) handles',
    spinnerHint:           'Use the arrows to set the time',
    previewNormal:         'What time is it?',
    previewReverse:        'Choose the correct clock',
    previewReverseWritten: 'quarter past three',
    mode: {
      analog:  { label: 'Analog',  description: 'Classic hands clock' },
      digital: { label: 'Digital', description: 'Hours and minutes display (24h)' },
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: '{rounds} questions · {options} options' },
      medium: { label: 'Medium', hint: '{rounds} questions · {options} options' },
      hard:   { label: 'Hard',   hint: '{rounds} questions · type the answer' },
    },
    complete: {
      title:           'Well done!',
      subtitle:        'You completed the clock exercise!',
      movesLabel:      'Attempts',
      timeLabel:       'Time',
      levelLabel:      'Level',
      playAgain:       'Play again',
      changeLevel:     'Change level',
      backToDashboard: '← Home',
    },
    time: {
      hourNames: ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'],
      articleSg: '',
      articlePl: '',
      minuteTemplates: {
        0:  "{hour} o'clock",
        5:  'five past {hour}',
        10: 'ten past {hour}',
        15: 'quarter past {hour}',
        20: 'twenty past {hour}',
        25: 'twenty-five past {hour}',
        30: 'half past {hour}',
        35: 'twenty-five to {nextHour}',
        40: 'twenty to {nextHour}',
        45: 'quarter to {nextHour}',
        50: 'ten to {nextHour}',
        55: 'five to {nextHour}',
      },
    },
  },

  crosswords: {
    title:                 'Crossword',
    subtitle:              'Connect letters to complete the crossword',
    backToDashboard:       '← Home',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start →',
    loading:               'Loading…',
    generationFailed:      'Could not generate the crossword. Please try again.',
    wordsFound:            'words',
    time:                  'time',
    instructionDrag:       'Drag the letters to form words',
    instructionComplete:   'Crossword complete!',
    hintBtn:               'Hint',
    bonusWordsLabel:       'Bonus words',
    bonusWordFlash:        'Bonus word!',
    difficulty: {
      easy:   { label: 'Easy',   hint: '3 short words' },
      medium: { label: 'Medium', hint: '5 words' },
      hard:   { label: 'Hard',   hint: '7 longer words' },
    },
    complete: {
      title:               'Crossword complete!',
      subtitle:            'You found all the words!',
      movesLabel:          'Words formed',
      timeLabel:           'Time',
      levelLabel:          'Level',
      hintsUsedLabel:      'Hints used',
      bonusWordsLabel:     'Bonus words',
      playAgain:           'Play again',
      changeLevel:         'Change level',
      backToDashboard:     '← Home',
    },
  },

  roman: {
    title:                 'Roman Numbers',
    subtitle:              'Translate the number into Roman numerals',
    backToDashboard:       '← Home',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start →',
    moves:                 'attempts',
    time:                  'time',
    questionCounter:       'Question {n} of {total}',
    questionPrompt:        'How do you write this in Roman numerals?',
    questionPromptHard:    'What number does this Roman numeral represent?',
    typeAnswer:            'Type the number…',
    submitBtn:             'Submit',
    wrongFeedback:         'Incorrect, try again',
    hardHint:              'Type the arabic number (e.g. V̄ = 5000)',
    previewLabel:          'Choose the correct Roman numeral',
    difficulty: {
      easy:   { label: 'Easy',   hint: 'Numbers 1–20 · 4 options' },
      medium: { label: 'Medium', hint: 'Numbers 1–1000 · 6 options' },
      hard:   { label: 'Hard',   hint: 'Numbers 1–10 000 · type the arabic number' },
    },
    complete: {
      title:           'Excellent!',
      subtitle:        'You completed Roman Numbers',
      movesLabel:      'Attempts',
      timeLabel:       'Time',
      levelLabel:      'Level',
      playAgain:       'Play again',
      changeLevel:     'Change level',
      backToDashboard: '← Home',
    },
  },

  synonyms: {
    title:                 'Synonyms & Antonyms',
    subtitle:              'Enrich your vocabulary',
    backToDashboard:       '← Home',
    chooseModeTitle:       'Choose a mode',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start →',
    loading:               'Loading words…',
    loadError:             'Could not load words. Please try again.',
    moves:                 'attempts',
    time:                  'time',
    questionCounter:       'Question {n} of {total}',
    questionPrompt:        'Synonym of:',
    questionPromptAntonym: 'Antonym of:',
    skipBtn:               'Skip →',
    selectAllCorrect:      'Select all correct answers',
    mode: {
      synonyms: { label: 'Synonyms', description: 'Find the synonym of each word' },
      antonyms: { label: 'Antonyms', description: 'Find the antonym of each word' },
      mixed:    { label: 'Mixed',    description: 'Mix of synonyms and antonyms' },
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: '4 options, one correct' },
      medium: { label: 'Medium', hint: 'Match 10 words with their answer' },
      hard:   { label: 'Hard',   hint: '10 options, multiple correct' },
    },
    complete: {
      title:           'Excellent!',
      subtitle:        'Vocabulary challenge complete!',
      movesLabel:      'Attempts',
      timeLabel:       'Time',
      levelLabel:      'Level',
      playAgain:       'Play again',
      changeLevel:     'Change level',
      backToDashboard: '← Home',
    },
  },

  puzzle: {
    title:                 'Puzzle',
    subtitle:              'Join the pieces and complete the image',
    backToDashboard:       '← Home',
    chooseThemeTitle:      'Choose a theme',
    chooseThemeHint:       'Select a theme to start',
    chooseModeTitle:       'Choose a mode',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start →',
    moves:                 'tries',
    time:                  'time',
    round:                 'Image',
    pairs:                 'Pairs',
    instructionPick:       'Which piece completes the image?',
    instructionPairs:      'Tap the two halves that form the same image',
    modes: {
      puzzle: { label: 'Puzzle', description: 'Find the missing piece' },
      pairs:  { label: 'Pairs',  description: 'Match the halves of each image' },
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: '2 pieces · 3 options' },
      medium: { label: 'Medium', hint: '4 pieces · 6 options' },
      hard:   { label: 'Hard',   hint: '6 pieces · 10 options' },
    },
    complete: {
      title:           'Well done!',
      subtitle:        'Puzzle complete!',
      movesLabel:      'Tries',
      timeLabel:       'Time',
      levelLabel:      'Level',
      playAgain:       'Play again',
      changeLevel:     'Change level',
      backToDashboard: '← Home',
    },
  },

  chess: {
    title:                 'Chess',
    subtitle:              'Learn and practise chess',
    backToDashboard:       '← Home',
    chooseModeTitle:       'Choose a mode',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start →',
    moves:                 'moves',
    time:                  'time',
    round:                 'round',
    modes: {
      recognize: { label: 'Recognize pieces', description: 'Identify the chess pieces' },
      movement:  { label: 'Movements',        description: 'Mark all the squares each piece can move to' },
      capture:   { label: 'Capture',          description: 'Capture all pawns in the fewest moves' },
      puzzle:    { label: 'Puzzles',          description: 'Solve classic chess puzzles' },
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: '4 pieces · simple positions' },
      medium: { label: 'Medium', hint: '6 pieces · full board' },
      hard:   { label: 'Hard',   hint: 'All pieces · advanced challenges' },
    },
    recognize: {
      instruction: 'Which piece is this?',
      correct:     'correct',
      colorWhite:  'White',
      colorBlack:  'Black',
      pieces: {
        k: 'King',
        q: 'Queen',
        r: 'Rook',
        b: 'Bishop',
        n: 'Knight',
        p: 'Pawn',
      },
    },
    movement: {
      instruction:  'Tap all the squares {piece} can move to',
      correct:      'correct',
      wrong:        'There are {n} valid squares — try again!',
      submitBtn:    'Check',
      nextBtn:      'Next →',
      finish:       'Finish →',
    },
    capture: {
      instruction:    'Select your piece and capture all the pawns',
      movesLabel:     'moves',
      capturedLabel:  'captured',
      optimalLabel:   'Target: {n} moves',
      yourMovesLabel: 'Your moves',
      bestLabel:      'Optimal',
      nextBtn:        'Next →',
      finishBtn:      'Finish →',
    },
    puzzle: {
      instruction:  'Find the best move for {color}',
      loading:      'Loading puzzle…',
      loadError:    'Could not load the puzzle. Check your connection.',
      solvedLabel:  'solved',
      solved:       'Correct! ♟',
      failed:       'Wrong move — try again.',
      thinking:     'Opponent is thinking…',
      white:        'White',
      black:        'Black',
      nextBtn:      'Next puzzle →',
      finishBtn:    'Finish →',
      retryBtn:     'Retry',
    },
    complete: {
      title:           'Well played!',
      subtitle:        'Game complete!',
      movesLabel:      'Moves',
      timeLabel:       'Time',
      levelLabel:      'Level',
      playAgain:       'Play again',
      changeLevel:     'Change level',
      backToDashboard: '← Home',
    },
  },

  numtarget: {
    title:                 'Magic Number',
    subtitle:              'Use the cards to reach the target number · the more you use, the better your score!',
    backToDashboard:       '← Home',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start →',
    goalLabel:             'Target',
    roundCounter:          'Round {n} of {total}',
    instruction:           'Tap cards and operators to build the calculation · more cards = better score!',
    submitBtn:             'Check',
    clearBtn:              'Clear all',
    deleteBtn:             '⌫',
    resultLabel:           'Result:',
    cardsUsed:             'Cards used',
    attemptsLabel:         'Attempts',
    ops: {
      add:        '+',
      sub:        '−',
      mul:        '×',
      div:        '÷',
      parenOpen:  '(',
      parenClose: ')',
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: '4 cards · add & subtract only' },
      medium: { label: 'Medium', hint: '6 cards · add, subtract & multiply' },
      hard:   { label: 'Hard',   hint: '7 cards · all operations' },
    },
    complete: {
      title:           'Brilliant!',
      subtitle:        'You reached the target number!',
      cardsUsedLabel:  'Cards used',
      timeLabel:       'Time',
      levelLabel:      'Level',
      attemptsLabel:   'Attempts',
      playAgain:       'Play again',
      changeLevel:     'Change level',
      backToDashboard: '← Home',
    },
  },

  memory: {
    title: 'Memory',
    subtitle: 'Find the matching pairs of cards',
    backToDashboard: '← Home',
    backToSetup: '←',
    chooseThemeTitle: 'Choose a theme',
    classicTheme: 'Classic',
    chooseModeTitle: 'Choose a mode',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn: 'Start Playing →',
    moves: 'moves',
    time: 'time',
    modes: {
      'images-images': { label: 'Images ↔ Images', description: 'Match two identical pictures' },
      'words-words':   { label: 'Words ↔ Words',   description: 'Match two identical words' },
      'images-words':  { label: 'Images ↔ Words',  description: 'Match a picture to its name' },
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: '6 pairs · 12 cards' },
      medium: { label: 'Medium', hint: '8 pairs · 16 cards' },
      hard:   { label: 'Hard',   hint: '12 pairs · 24 cards' },
    },
    complete: {
      title: 'Well done!',
      subtitle: 'You matched all the pairs',
      movesLabel: 'Moves',
      timeLabel: 'Time',
      levelLabel: 'Level',
      playAgain: 'Play Again',
      changeMode: 'Change Mode',
      backToDashboard: '← Home',
    },
  },
  cutlery: {
    title:                 'Cutlery',
    subtitle:              'Sort or set the table',
    backToDashboard:       '← Home',
    chooseModeTitle:       'Choose a mode',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start →',
    moves:                 'attempts',
    time:                  'time',
    instructionPick:       'Select a piece of cutlery',
    instructionPlace:      'Now place it in the right compartment',
    instructionPickTable:  'Select a piece of cutlery',
    instructionPlaceTable: 'Place it at the right setting',
    menuTitle:             "Guest's order",
    closeMenuBtn:          'Close',
    slots: {
      spoon:    'Spoons',
      fork:     'Forks',
      knife:    'Knives',
      teaspoon: 'Teaspoons',
    },
    comandaTitle:         "Table's order",
    drawerTapInstruction: 'Tap drawer to take · Tap piece to return',
    checkBtn:             'Check →',
    countWrongFeedback:   'Some counts are wrong — review and try again',
    modes: {
      drawer: { label: 'Sort the drawer',       description: 'Sort cutlery pieces into their compartments' },
      table:  { label: 'Set the table',         description: 'Read each menu and place the right cutlery' },
      count:  { label: 'Count the cutlery',     description: "Count the cutlery the table needs" },
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: 'Simple' },
      medium: { label: 'Medium', hint: 'More variety' },
      hard:   { label: 'Hard',   hint: 'Teaspoon included' },
    },
    complete: {
      title:           'Well done!',
      subtitle:        'You placed all the cutlery',
      subtitleTable:   'You set the table correctly',
      subtitleCount:   'You counted the cutlery correctly',
      movesLabel:      'Attempts',
      timeLabel:       'Time',
      levelLabel:      'Level',
      playAgain:       'Play Again',
      changeLevel:     'Change Level',
      backToDashboard: '← Home',
    },
  },

  wordmatch: {
    title:                 'Words',
    subtitle:              'Test your vocabulary with images, definitions and anagrams',
    backToDashboard:       '← Home',
    chooseThemeTitle:      'Choose a theme',
    classicTheme:          'Classic',
    chooseModeTitle:       'Choose a mode',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start →',
    moves:                 'attempts',
    time:                  'time',
    questionCounter:       'Question {n} of {total}',
    questionPrompt:        'What is the word?',
    anagramLabel:          'Unscramble the letters',
    hintLetterCount:       'Number of letters',
    hintFirstLetter:       'First letter',
    letters:               'letters',
    typeAnswer:            'Type your answer...',
    submitBtn:             'Confirm',
    wrongFeedback:         'Incorrect, try again',
    mode: {
      images:      { label: 'Images',      description: 'Look at the image and choose the word' },
      definitions: { label: 'Definitions', description: 'Read the definition and choose the word' },
      anagram:     { label: 'Anagram',     description: 'Unscramble the letters to find the word' },
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: '4 options' },
      medium: { label: 'Medium', hint: '6 options' },
      hard:   { label: 'Hard',   hint: 'Type the answer' },
    },
    complete: {
      title:           'Well done!',
      subtitle:        'You completed all the questions',
      movesLabel:      'Attempts',
      timeLabel:       'Time',
      levelLabel:      'Level',
      playAgain:       'Play Again',
      changeLevel:     'Change Level',
      backToDashboard: '← Home',
    },
  },

  wordsearch: {
    title:                 'Word Search',
    subtitle:              'Find all the hidden words',
    backToDashboard:       '← Home',
    chooseThemeTitle:      'Choose a theme',
    classicTheme:          'Classic',
    chooseModeTitle:       'Choose a mode',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start →',
    wordsFound:            'words',
    time:                  'time',
    instructionStart:      'Tap letters one by one to select a word',
    instructionContinue:   'Keep going in a straight line, or tap another letter to start over',
    modes: {
      simple:    { label: 'Horizontal & vertical', description: 'Words go across or down' },
      diagonals: { label: 'With diagonals',        description: 'Words can also go diagonally' },
      reversed:  { label: 'Reversed',              description: 'Words can go in any direction, even backwards' },
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: '8×8 grid · 5 words' },
      medium: { label: 'Medium', hint: '12×12 grid · 8 words' },
      hard:   { label: 'Hard',   hint: '16×16 grid · 12 words' },
    },
    complete: {
      title:           'Well done!',
      subtitle:        'You found all the words',
      wordsLabel:      'Words',
      timeLabel:       'Time',
      levelLabel:      'Level',
      playAgain:       'Play Again',
      changeLevel:     'Change Level',
      backToDashboard: '← Home',
    },
  },

  sudoku: {
    title:                 'Sudoku',
    subtitle:              'Fill the grid without repeating any number',
    backToDashboard:       '← Home',
    chooseModeTitle:       'Choose a mode',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start Playing →',
    errors:                'errors',
    time:                  'time',
    notes:                 'Notes',
    erase:                 'Erase',
    instructionSelect:     'Tap an empty cell to select it',
    instructionEnter:      'Tap a number to place it in the cell',
    modes: {
      standard: { label: 'Classic 9x9', description: 'The classic sudoku grid' },
      mini:     { label: 'Mini 4x4',    description: 'A simpler version with numbers 1 to 4' },
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: 'Many clues' },
      medium: { label: 'Medium', hint: 'Moderate clues' },
      hard:   { label: 'Hard',   hint: 'Fewer clues' },
    },
    complete: {
      title:           'Excellent!',
      subtitle:        'You solved the sudoku!',
      movesLabel:      'Moves',
      errorsLabel:     'Errors',
      timeLabel:       'Time',
      levelLabel:      'Level',
      playAgain:       'Play Again',
      changeLevel:     'Change Level',
      backToDashboard: '← Home',
    },
  },

  maths: {
    title:                 'Maths',
    subtitle:              'Solve basic mathematical operations',
    backToDashboard:       '← Home',
    chooseOpsTitle:        'Choose operations',
    chooseOpsHint:         'You can combine multiple operations',
    chooseDifficultyTitle: 'Choose difficulty',
    startBtn:              'Start Playing →',
    correct:               'correct',
    time:                  'time',
    questionCounter:       'Question {n} of {total}',
    instructionPick:       'What is the answer?',
    instructionCorrect:    'Correct!',
    operations: {
      addition:       'Addition',
      subtraction:    'Subtraction',
      multiplication: 'Multiplication',
      division:       'Division',
    },
    difficulty: {
      easy:   { label: 'Easy',   hint: 'Numbers up to 10 · 8 questions' },
      medium: { label: 'Medium', hint: 'Numbers up to 20 · 10 questions' },
      hard:   { label: 'Hard',   hint: 'Numbers up to 50 · 12 questions' },
    },
    complete: {
      title:           'Well done!',
      subtitle:        'You completed the maths exercise',
      correctLabel:    'Answers',
      timeLabel:       'Time',
      levelLabel:      'Level',
      playAgain:       'Play Again',
      changeLevel:     'Change Level',
      backToDashboard: '← Home',
    },
  },

  subscription: {
    title:       'Plans',
    subtitle:    'Choose the plan that suits you best',
    backBtn:         '← Back',
    currentPlan:     'Your current plan',
    includedPlan:    'Included in your plan',
    upgradeBtn:      'Choose plan',
    manageBtn:       'Manage subscription',
    loadingCheckout: 'Redirecting to payment…',
    successBanner:   'Subscription activated! Welcome to your new plan.',
    canceledBanner:  'Payment cancelled. Your plan has not changed.',
    errorBanner:     'Something went wrong. Please try again.',
    tiers: {
      basic: {
        name:        'Basic',
        price:       'Free',
        period:      '',
        description: 'Everything you need to start training your mind.',
        features:    ['Basic activities 🧠', 'Unlimited games', 'Progress statistics'],
      },
      plus: {
        name:        'Plus',
        price:       '€3.90',
        period:      '/ month',
        description: 'More activities to keep your mind active.',
        features:    ['Everything in Basic', 'Functional activities 🛠️', 'Printable activities 🖨️', 'Activity notebooks 📚'],
      },
      premium: {
        name:        'Premium',
        price:       '€5.90',
        period:      '/ month',
        description: 'The complete cognitive wellness experience.',
        features:    ['Everything in Plus', 'Custom notebooks 📓', 'Professional Mode 👩‍⚕️', 'Early access to new activities'],
      },
    },
  },

  downloads: {
    title:           'Downloads',
    intro:           'Download activities as PDF to print and enjoy screen-free.',
    printablesTab:   'Printables',
    notebooksTab:    'Notebooks',
    upgradeBtn:      '🔒 Upgrade subscription',
    notReady:        'Coming soon',
    downloading:     'Downloading…',
    downloadBtn:     '⬇ Download PDF',
    loading:         'Loading…',
    emptyPrintables:       'Printable activities will be available soon.',
    emptyNotebooks:        'Notebooks will be available soon.',
    notebookMeta:          'Notebook · {n} activities',
    errorDownload:         'Download error',
    myNotebooksTitle:      'My notebooks',
    libraryTitle:          'Library',
    emptyMyNotebooks:      'You have no generated notebooks yet.',
    generateNotebookBtn:   'Generate my notebook',
    premiumRequired:       'Subscribe to Premium to generate your own notebooks.',
    upgradeToPremiumBtn:   'See Premium plans',
    builderTitle:          'Create my notebook',
    builderActivities:     'Activities',
    builderAddActivity:    'Add activity',
    builderSelectActivity: 'Select an activity',
    builderAddWordsearch:  'Word search',
    builderAddSudoku:      'Sudoku',
    builderAddMaths:       'Maths',
    builderAddAttention:      'Attention',
    builderAddSymmetry:       'Symmetry',
    builderAddWhatsMissing:   "What's missing?",
    builderAddRoman:           'Roman Numbers',
    builderDifficultyLabel:    'Difficulty',
    builderPagesLabel:         'Pages',
    builderAddHours:           'What time is it?',
    builderAddSynonyms:        'Synonyms & Antonyms',
    builderAddPuzzle:          'Puzzle (match halves)',
    builderCountLabel:         'Count',
    builderSearchModeLabel:    'Search mode',
    builderGridLabel:          'Grid',
    builderModeLabel:          'Mode',
    builderOperationLabel:     'Operation',
    builderTypeLabel:          'Type',
    builderClockTypeLabel:     'Clock type',
    builderWriteModeLabel:     'Write mode',
    builderSymmetryModeLabel:  'Symmetry',
    builderThemesOptionalLabel: 'Themes (optional, all used if none selected)',
    builderNoThemesMsg:        'No themes — default words used',
    builderDiffEasy:           'Easy',
    builderDiffMedium:         'Medium',
    builderDiffHard:           'Hard',
    builderWsModeSimple:       'Simple (→ ↓)',
    builderWsModeDiagonals:    'Diagonals',
    builderWsModeAllDirs:      'All directions',
    builderSdModeClassic:      'Classic 9×9',
    builderSdModeMini:         'Mini 4×4',
    builderMtOpAddition:       'Addition (+)',
    builderMtOpSubtraction:    'Subtraction (−)',
    builderMtOpMultiplication: 'Multiplication (×)',
    builderMtOpDivision:       'Division (÷)',
    builderAtModeWords:        'Words',
    builderAtModeNumbers:      'Numbers',
    builderSymModeVertical:    'Vertical ↔',
    builderSymModeHorizontal:  'Horizontal ↕',
    builderSymModeQuadrant:    'Quadrant ✦',
    builderWmModeEmojis:       'Emojis',
    builderWmModeShapes:       'Shapes',
    builderHrsModeJoin:        'Match columns',
    builderHrsModeWrite:       'Write / Draw',
    builderHrsClockAnalog:     'Analog',
    builderHrsClockDigital:    'Digital',
    builderHrsClockMixed:      'Mixed',
    builderHrsWriteWords:      'Write the time in words',
    builderHrsWriteDraw:       'Draw the clock hands',
    builderSynModeSynonyms:    'Synonyms',
    builderSynModeAntonyms:    'Antonyms',
    builderSynModeMixed:       'Mixed',
    builderSynPrintWrite:      'Write',
    builderSynPrintJoin:       'Match columns',
    builderRomPrintWrite:      'Write',
    builderRomPrintJoin:       'Match columns',
    builderGenerate:       'Generate notebook',
    builderGenerating:     'Generating…',
    statusGenerating:      'Generating PDF…',
    statusFailed:          'Generation failed',
    retryBtn:              'Retry',
    deleteBtn:             'Delete',
    typeLabels: {
      memory_cards:     'Memory cards',
      memory_reference: 'Image-word table',
      definitions:      'Definition cards',
    },
    tierLabels: {
      BASIC:   'Basic',
      PLUS:    'Plus',
      PREMIUM: 'Premium',
    },
    diffLabels: {
      easy:   'Easy',
      medium: 'Medium',
      hard:   'Hard',
    },
  },

  adminHub: {
    title:             'Administration',
    back:              '← Home',
    backToAdmin:       '← Admin',
    themesTitle:       'Themes',
    themesDesc:        'Manage image themes and generate content with AI',
    usersTitle:        'Users',
    usersDesc:         'View users, search by email and manage subscriptions',
    printablesTitle:   'Printables',
    printablesDesc:    'Create and manage printables and activity notebooks',
    activitiesTitle:   'Activities',
    activitiesDesc:    'Manage the tier and visibility of each activity',
    statsTitle:        'Statistics',
    statsDesc:         'Activity usage, sessions, and generated notebooks',
  },

  adminStats: {
    title:             'Statistics',
    back:              '← Admin',
    totalUsers:        'Total users',
    activeThisWeek:    'Active this week',
    totalSessions:     'Total sessions',
    sessionsThisWeek:  'Sessions this week',
    sessionsByActivity:'Sessions by activity',
    sessionsByCategory:'Sessions by category',
    abandonRate:       'Abandon rate',
    avgDuration:       'Average duration per activity',
    notebooksByActivity: 'Notebooks by activity',
    topThemes:         'Top themes',
    noData:            'No data',
    abandonRateHeader: 'Activity',
    avgDurationHeader: 'Activity',
    themeHeader:       'Theme',
    countHeader:       'Count',
    activityHeader:    'Activity',
    categoryHeader:    'Category',
  },

  professional: {
    hubTitle:           'Professional Mode',
    hubSubtitle:        'Manage patient profiles, build activity playbooks, and track each patient\'s progress.',
    enableBtn:          'Enable professional mode',
    enableDesc:         'Professional mode lets you create patient profiles and configure personalised activity sequences.',
    premiumRequired:    'Premium subscription required',
    premiumRequiredDesc:'Professional mode is exclusively available to users with a Premium subscription.',
    goToPremium:        'View plans',
    back:               '← Home',

    profilesTitle:      'Profiles',
    newProfile:         'New profile',
    noProfiles:         'No profiles yet. Create one to get started.',

    profileName:        'Patient name',
    profileNotes:       'Notes (optional)',
    profilePin:         'PIN (4 digits)',
    profilePinHint:     'The professional will enter this PIN at the end of each session.',
    createProfile:      'Create profile',
    editProfile:        'Edit profile',
    saveProfile:        'Save',
    deleteProfile:      'Delete profile',
    confirmDelete:      'Are you sure you want to delete this profile? All its playbooks and sessions will be deleted.',
    cancel:             'Cancel',

    playbooksTitle:     'Playbooks',
    newPlaybook:        'New playbook',
    noPlaybooks:        'This profile has no playbooks yet.',
    playbookName:       'Playbook name',
    createPlaybook:     'Create playbook',
    editPlaybook:       'Rename',
    savePlaybook:       'Save',
    deletePlaybook:     'Delete playbook',
    startSession:       'Start session',

    editorTitle:        'Edit playbook',
    addActivity:        'Add activity',
    noItems:            'This playbook is empty. Add activities to get started.',
    saveChanges:        'Save changes',
    difficulty:         'Difficulty',
    difficultyEasy:     'Easy',
    difficultyMedium:   'Medium',
    difficultyHard:     'Hard',
    theme:              'Theme',
    themeClassic:       'Classic',
    themeNone:          'No theme',

    sessionProgress:    'Activity {current} of {total}',
    sessionNext:        'Next →',
    sessionFinish:      'Finish session',
    sessionLocked:      'Session complete!',
    sessionLockedDesc:  'All activities are done. The professional must enter the PIN to continue.',
    sessionPinPrompt:   'Enter PIN',
    sessionUnlock:      'Unlock',
    sessionWrongPin:    'Incorrect PIN. Please try again.',
    exitTitle:          'Exit session',
    exitDesc:           'Enter the profile PIN to exit.',
    exitCancel:         'Back to activity',

    resultsTitle:    'Activity complete!',
    resultsDuration: 'Time',
    resultsScore:    'Correct',
    resultsMoves:    'Moves',
    resultsNext:     'Next activity',

    statsTitle:         'Statistics',
    statsFilter:        'Filter by profile',
    statsAllProfiles:   'All profiles',
    statsTotalSessions: 'Total sessions',
    statsLastSession:   'Last session',
    statsNoData:        'No data yet for this profile.',
    statsScore:         'Score',
    statsDuration:      'Duration (s)',
    statsDate:          'Date',
    statsScoreChart:    'Score per session',
    statsDurationChart: 'Duration per session',
    statsShowTable:     'Show table',
    statsShowChart:     'Show chart',
    statsActivities:    'Activities',
  },

  items: [
    { id: 1,  emoji: '🌸', word: 'Blossom',   definition: 'A flower, or the mass of flowers on a tree' },
    { id: 2,  emoji: '🦋', word: 'Butterfly', definition: 'An insect with large, colorful wings' },
    { id: 3,  emoji: '🌊', word: 'Wave',      definition: 'A moving ridge of water in the sea' },
    { id: 4,  emoji: '🏔️', word: 'Mountain',  definition: 'A large natural rise of the land surface' },
    { id: 5,  emoji: '🌙', word: 'Moon',      definition: 'The natural satellite visible at night' },
    { id: 6,  emoji: '⭐', word: 'Star',      definition: 'A luminous body of gas visible in the night sky' },
    { id: 7,  emoji: '🌿', word: 'Fern',      definition: 'A flowerless plant with feathery green leaves' },
    { id: 8,  emoji: '🦅', word: 'Eagle',     definition: 'A large, powerful bird of prey' },
    { id: 9,  emoji: '🐠', word: 'Clownfish', definition: 'A small, colorful fish living among sea anemones' },
    { id: 10, emoji: '🌺', word: 'Hibiscus',  definition: 'A tropical shrub with large, showy flowers' },
    { id: 11, emoji: '🎵', word: 'Melody',    definition: 'A sequence of musical notes forming a tune' },
    { id: 12, emoji: '📖', word: 'Story',     definition: 'An account of events, real or imagined' },
  ],
  terms: {
    title:   'Terms and Conditions',
    backBtn: 'Back',
  },
  common: {
    saveError:             'Could not save your progress. Please check your connection.',
    dismissError:          'Dismiss',
    resumeTitle:           'Continue your last game?',
    sessionResumeTitle:    'Session in progress',
    sessionResumeDesc:     'You have a session in progress. Would you like to continue where you left off?',
    sessionResumeContinue: 'Continue',
    sessionResumeNew:      'Start new',
  },
  stats: {
    title:          'My Statistics',
    avgScore:       'Avg. score',
    bestActivity:   'Best activity',
    totalSessions:  'Sessions played',
    radarTitle:     'Performance by category',
    radarAriaLabel: 'Radar chart showing performance by category',
    evolutionTitle: 'Score evolution',
    durationTitle:  'Time spent per activity',
    noData:         'Play some games to see your statistics here.',
    viewStats:      'View detailed statistics →',
    thisWeek:       'This week',
    lastWeek:       'Last week',
    filterBy:       'Filter dashboard',
    dateRange:      'Date range',
    activities:     'Activities',
    categoriesLabel: 'Categories',
    allActivities:  'All activities',
    allCategories:  'All categories',
    datePresets: {
      today:          'Today',
      'this-week':   'This week',
      'this-month':  'This month',
      'last-3-months': 'Last 3 months',
      'last-6-months': 'Last 6 months',
      'this-year':   'This year',
      'all-time':    'All time',
    },
    categories: {
      memory:           'Memory',
      language:         'Language',
      'daily-life':     'Daily life',
      logic:            'Logic',
      maths:            'Maths',
      attention:        'Attention',
      'visual-spatial': 'Visual-spatial',
    },
  },
}
